import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Settings, Plus, Info, Sun, Archive, FolderOpen, Tag, Shield, Sparkles } from 'lucide-react';
import HelpDialog from './HelpDialog';

const MAIN_HELP_CONTENT = `If you are completely new to UpNext, here's a quick overview of how the app works!

**<u>Tasks</u>** can be created from anywhere using the button in the app header (or by pressing ctrl + alt + T on Windows or option + command + T on Mac). When adding a task, you must give it a name and select an option for each of its three tag categories: Type, Domain, and Energy. The remainder of the information fields are optional (such as adding subtasks or a due date).

**<u>Tags</u>** are the core feature of the UpNext task prioritization algorithm and they can be added, edited, reordered, or deleted from the **<u>Tag Management</u>** page. Importantly, the individual tags within each of the three categories (Type, Domain, and Energy) are ordered from highest priority at the top to lowest priority at the bottom. Reordering tags here also updates the task priority order in all other lists (Task Storage and the Active List).

Once created, all tasks are automatically grouped and sorted in the **<u>Task Storage</u>** page based on the tags you gave it. This is designed to be a catch-all holding place for all those to-do's floating around inside your head! Thanks to the prioritization tags, your most important tasks will naturally rise to the top.

The **<u>Active Task</u>** page, on the other hand, is a place to display only the tasks you have chosen to be your current focus. There are several ways a task can end up here (see the Active page for details), but the main thing to know is that this will serve as your primary "To-Do" list. There is also a handy Focus Mode to help you hone in on your most important tasks one at a time.

Lastly, checking off a task as completed will automatically move it from Task Storage to the **<u>Task Archive</u>** page. From here, tasks can be "uncompleted" (moving them back to Storage) or deleted permanently.

And there you have it! Happy task making, sorting, and doing. 😊`;

export default function TopBar({ activeTab, onChange, onSettingsClick, onAddClick, onAboutClick, user }) {
  return (
    <>
      {/* Top Header - styled like landing page */}
      <header
        className="sticky top-0 z-30 bg-slate-900 border-b border-slate-700/50"
        data-testid="top-bar"
      >
        <div className="max-w-6xl mx-auto flex items-center gap-3 py-3 px-4 sm:px-6">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="brand">
            <Sparkles className="h-5 w-5" style={{ color: 'var(--color-primary)' }} />
            <span className="h-font text-lg font-semibold text-white">UpNext To-Do</span>
          </a>
          {/* Add Task button on left - desktop only */}
          <Button
            onClick={onAddClick}
            aria-label="Add Task"
            data-testid="add-task-button-header-left"
            className="hidden md:flex items-center gap-2 text-white"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <Plus size={16} />
            Add Task
          </Button>
          {/* Desktop tabs - on right side */}
          <div className="ml-auto hidden md:flex items-center gap-3">
            <Tabs value={activeTab} onValueChange={onChange}>
              <TabsList className="bg-transparent gap-1">
                <TabsTrigger value="active" data-testid="nav-active" className="text-slate-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  Active
                </TabsTrigger>
                <TabsTrigger value="storage" data-testid="nav-storage" className="text-slate-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  Storage
                </TabsTrigger>
                <TabsTrigger value="archive" data-testid="nav-archive" className="text-slate-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  Archive
                </TabsTrigger>
                <TabsTrigger value="tags" data-testid="nav-tags" className="text-slate-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  Tags
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {/* Desktop icons */}
          <div className="hidden md:flex items-center gap-0.5 flex-shrink-0">
            <HelpDialog 
              title="How to use UpNext To-Do" 
              content={MAIN_HELP_CONTENT}
              iconSize={20}
              ariaLabel="Help"
              iconClassName="text-slate-300 hover:text-black hover:bg-slate-200"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={onAboutClick}
              aria-label="About"
              data-testid="about-button"
              className="text-slate-300 hover:text-black hover:bg-slate-200"
            >
              <Info size={20} />
            </Button>
            {user?.is_admin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onChange('admin')}
                aria-label="Admin"
                data-testid="admin-button"
                className="text-slate-300 hover:text-black hover:bg-slate-200"
              >
                <Shield size={20} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onSettingsClick}
              aria-label="Settings"
              data-testid="settings-button"
              className="text-slate-300 hover:text-black hover:bg-slate-200"
            >
              <Settings size={20} />
            </Button>
          </div>
          {/* Mobile icons - only Help, About, Admin (if admin), and Settings */}
          <div className="ml-auto md:hidden flex items-center gap-0.5">
            <HelpDialog 
              title="How to use UpNext To-Do" 
              content={MAIN_HELP_CONTENT}
              iconSize={20}
              ariaLabel="Help"
              iconClassName="text-slate-300 hover:text-black hover:bg-slate-200"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={onAboutClick}
              aria-label="About"
              data-testid="about-button-mobile"
              className="text-slate-300 hover:text-black hover:bg-slate-200"
            >
              <Info size={20} />
            </Button>
            {user?.is_admin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onChange('admin')}
                aria-label="Admin"
                data-testid="admin-button-mobile"
                className="text-slate-300 hover:text-black hover:bg-slate-200"
              >
                <Shield size={20} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onSettingsClick}
              aria-label="Settings"
              data-testid="settings-button-mobile"
              className="text-slate-300 hover:text-black hover:bg-slate-200"
            >
              <Settings size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar - styled like landing page */}
      <div 
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 border-t border-slate-700/50 shadow-lg backdrop-blur-md" 
        style={{
          position: 'fixed',
          bottom: 'env(safe-area-inset-bottom, 0)',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden'
        }}
      >
        <nav className="flex items-center justify-between py-2 px-6 gap-2">
          <button
            onClick={() => onChange('active')}
            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
              activeTab === 'active' 
                ? 'text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
            style={activeTab === 'active' ? { backgroundColor: 'var(--color-primary)' } : {}}
            data-testid="bottom-nav-active"
          >
            <Sun size={20} />
            <span className="text-xs font-medium">Active</span>
          </button>
          
          <button
            onClick={() => onChange('storage')}
            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
              activeTab === 'storage' 
                ? 'text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
            style={activeTab === 'storage' ? { backgroundColor: 'var(--color-primary)' } : {}}
            data-testid="bottom-nav-storage"
          >
            <FolderOpen size={20} />
            <span className="text-xs font-medium">Storage</span>
          </button>
          
          <button
            onClick={() => onChange('archive')}
            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
              activeTab === 'archive' 
                ? 'text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
            style={activeTab === 'archive' ? { backgroundColor: 'var(--color-primary)' } : {}}
            data-testid="bottom-nav-archive"
          >
            <Archive size={20} />
            <span className="text-xs font-medium">Archive</span>
          </button>
          
          <button
            onClick={() => onChange('tags')}
            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
              activeTab === 'tags' 
                ? 'text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
            style={activeTab === 'tags' ? { backgroundColor: 'var(--color-primary)' } : {}}
            data-testid="bottom-nav-tags"
          >
            <Tag size={20} />
            <span className="text-xs font-medium">Tags</span>
          </button>
          
          <button
            onClick={onAddClick}
            className="flex-1 flex flex-col items-center justify-center py-2 rounded-lg transition-colors text-white"
            style={{ backgroundColor: 'var(--color-primary)' }}
            aria-label="Add Task"
            data-testid="bottom-nav-add-task"
          >
            <Plus size={20} />
          </button>
        </nav>
      </div>
    </>
  );
}
